import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';

import { Text, TextType } from 'ui-components/components/text';

import { LogService } from '../../../../services/log-service';
import { setShippyPendingPrefill } from '../../../../store/shippy/shippy.actions';
import { resolveCommand } from '../../command-resolver';
import { useHandlerTracking } from '../../hooks/use-handler-tracking';
import { messages } from '../../messages';
import { buildRouteWithHighlight } from '../../route-utils';
import { getCommand } from '../../navigation-manifest';
import { CAPABILITY_IDS } from '../capabilities-registry';
import style from '../../style.module.less';

type NavigationParameters = {
  commandId: string;
  parameters?: Record<string, unknown>;
};

type NavigationHandlerProps = {
  parameters: NavigationParameters;
  conversationId: string;
  turnNumber: number;
};

const buildPendingPrefill = (commandId: string, commandParams: Record<string, unknown>) => {
  const command = getCommand(commandId);
  const prefillConfig = command?.prefillConfig;
  if (!prefillConfig) return null;

  const data = commandParams[prefillConfig.paramKey];
  if (!data || typeof data !== 'object' || Object.keys(data as object).length === 0) return null;

  return { data, target: prefillConfig.target, commandId };
};

export const NavigationHandler = ({ parameters, conversationId, turnNumber }: NavigationHandlerProps) => {
  const dispatch = useDispatch();

  const { commandId, parameters: commandParams = {} } = parameters;

  const command = commandId ? getCommand(commandId) : null;
  const prefillParamKey = command?.prefillConfig?.paramKey;
  const routeParams = prefillParamKey
    ? (({ [prefillParamKey]: _, ...rest }) => rest)(commandParams as Record<string, string>)
    : (commandParams as Record<string, string>);
  const resolved = commandId ? resolveCommand(commandId, routeParams) : null;

  const route = resolved?.route ?? null;
  const highlightSelector = resolved?.highlightSelector ?? null;
  const isFallbackRoute = resolved?.isFallbackRoute ?? false;
  const finalRoute = route ? buildRouteWithHighlight(route, highlightSelector) : null;

  const { trackAction } = useHandlerTracking({
    capabilityId: CAPABILITY_IDS.NAVIGATION,
    conversationId,
    turnNumber,
    parameters: parameters as unknown as Record<string, unknown>,
    isValid: !!finalRoute,
  });

  const handleClick = useCallback(() => {
    trackAction();
    try {
      const pendingPrefill = commandId ? buildPendingPrefill(commandId, commandParams) : null;
      if (pendingPrefill) {
        dispatch(setShippyPendingPrefill(pendingPrefill));
      }
    } catch (error) {
      LogService.logError(new Error('Shippy pre-navigate failed', { cause: error }));
    }
  }, [dispatch, commandId, commandParams, trackAction]);

  if (!finalRoute) {
    return <Text textType={TextType.Caption} value={messages.capabilityError} />;
  }

  return (
    <div className={style.commandLink}>
      {isFallbackRoute ? <Text textType={TextType.Paragraph} value={messages.commandFallbackMessage} /> : null}
      <Link onClick={handleClick} to={finalRoute}>
        <Text textType={TextType.Paragraph} value={messages.commandLinkButton} />
      </Link>
    </div>
  );
};
